import React, { useState, useEffect, useContext } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { FaCalendar, FaFileDownload, FaFileExcel } from 'react-icons/fa';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Communicator } from '../../tools/Communication';
import { getEnvironment } from '@/utils/env';
import { toCsvBlob } from '@/utils/data';
import { useHistory } from 'react-router';
import { download, downloadJsonDataAsExcelFile } from '@/utils/download';
import {
  CanAccessPartnerData,
  IsVytalEmployee,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { StoreAdminFragmentFragment } from '../../graphql/fragments/storeAdminInfo.generated';
import { useStoreQuery } from '../../graphql/queries/storeAdminInfo.generated';
import { NonUsageFeeInfo } from '../../graphql/generatedTypes';
import { centsToPrice } from '@/utils/formatter';
import { Currency } from '@/utils/price';
import { getCurrentDatePlusDays } from '@/utils/date';
import { MonthPicker } from '@mantine/dates';
import { Popover } from '@mantine/core';
import { ContentSection } from '../../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';

interface Stock {
  id: number;
  name: string;
  amount: number;
}

type StockTabProps = {
  vytalStoreId: string;
  pickpackStoreId: string;
  title: string;
};

export const StockTab = (props: StockTabProps) => {
  const viewer = useContext(ViewerContext);
  const allowedToAccess = CanAccessPartnerData(viewer);
  const [stock, setStock] = useState([] as Stock[]);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const [date, setDate] = useState<Date | null>(getCurrentDatePlusDays(0));

  const [store, setStore] = useState<StoreAdminFragmentFragment | null>(
    null as any,
  );
  if (allowedToAccess) {
    useStoreQuery({
      fetchPolicy: 'no-cache',
      skip: !props.pickpackStoreId,
      variables: { id: props.pickpackStoreId || '' },
      onCompleted: (storeData) => {
        // @ts-ignore
        if (storeData != null && storeData.store != null) {
          setStore(storeData.store);
        }
      },
    });
  }

  useEffect(() => {
    setIsLoading(true);
    const storeId = props.vytalStoreId;
    Communicator.Get('api/3/StoreAnalytics/GetStoreStock', { storeId })
      .then((res) => res.json())
      .then((rows) => {
        if (Array.isArray(rows)) {
          setStock(
            rows.map((r, index) => ({
              id: index,
              name: r.containerTypeName,
              amount: r.amount,
            })),
          );
          setIsLoading(false);
        }
      });
  }, []);

  const toJson = (data: string) => {
    if (data === 'stock') {
      return stock.map((s) => {
        const obj: any = {};
        obj[i18n.t('Common:name')] = s.name;
        obj[i18n.t('Common:amount')] = s.amount;
        return obj;
      });
    } else if (
      data === 'nonUsageFee' &&
      store != null &&
      store.all_non_usage_fee_info != null &&
      store.non_usage_fee_info != null
    ) {
      return store.non_usage_fee_info.map((s) => {
        const objects = store.all_non_usage_fee_info.previous_info.map((i) => ({
          [i18n.t('Common:year')]: getCurrentDatePlusDays(0).getFullYear(),
          [i18n.t('Common:month')]: getCurrentDatePlusDays(0).getMonth() + 1,
          [i18n.t('Common:name')]: 'Total',
          [i18n.t('Common:stock')]: i.total.stock,
          [i18n.t('Stock:avg_weekly_checkouts')]: i.total.avg_weekly_checkouts,
          [i18n.t('Stock:allowance')]: i.total.allowance,
          [i18n.t('Stock:nonReturnCost')]: i.total.non_return_cost,
          [i18n.t('Stock:netReceiver')]: i.is_net_receiver,
        }));
        const obj: any = {};
        if (s != null) {
          obj[i18n.t('Common:year')] = getCurrentDatePlusDays(0).getFullYear();
          obj[i18n.t('Common:month')] =
            getCurrentDatePlusDays(0).getMonth() + 1;
          obj[i18n.t('Common:name')] = s.container_type_name;
          obj[i18n.t('Common:stock')] = s.stock;
          obj[i18n.t('Stock:avg_weekly_checkouts')] = s.avg_weekly_checkouts;
          obj[i18n.t('Stock:allowance')] = s.allowance || '-';
          obj[i18n.t('Stock:nonReturnCost')] = s.non_return_cost || '-';
          obj[i18n.t('Stock:netReceiver')] = store.is_net_receiver || false;
        }
        return obj;
      });
    }
    return [];
  };

  const exportCSV = (data: string) => {
    const blob = toCsvBlob(toJson(data));
    download(
      blob,
      `${data}_${new Date(Date.now()).toISOString().substring(0, 10)}.csv`,
    );
  };

  const exportExcel = (data: string) => {
    const jsonData = toJson(data);
    downloadJsonDataAsExcelFile(jsonData, data);
  };

  function getInfo() {
    if (
      date?.getFullYear() === getCurrentDatePlusDays(0).getFullYear() &&
      date?.getMonth() === getCurrentDatePlusDays(0).getMonth()
    ) {
      return store?.all_non_usage_fee_info.current_info;
    }
    const previousInfo = store?.all_non_usage_fee_info.previous_info.find(
      (i) => i.year === date?.getFullYear() && i.month === date.getMonth() + 1,
    );
    if (previousInfo != null) {
      return [
        {
          ...previousInfo.total,
          container_type_name: 'Total',
        },
      ];
    }
    return [];
  }

  function getMinDate() {
    const previousInfo = store?.all_non_usage_fee_info.previous_info;
    if (previousInfo == null || previousInfo.length === 0) {
      return getCurrentDatePlusDays(0);
    }
    const sortedInfo = [...previousInfo].sort((a, b) => {
      if (a.year === b.year) {
        return a.month - b.month;
      } else {
        return a.year - b.year;
      }
    });
    return new Date(sortedInfo[0].year, sortedInfo[0].month - 1, 1);
  }

  function isNetReceiver() {
    if (
      date?.getFullYear() === getCurrentDatePlusDays(0).getFullYear() &&
      date?.getMonth() === getCurrentDatePlusDays(0).getMonth()
    ) {
      return store?.is_net_receiver;
    }
    const previousInfo = store?.all_non_usage_fee_info.previous_info.find(
      (i) => i.year === date?.getFullYear() && i.month === date.getMonth() + 1,
    );
    if (previousInfo != null) {
      return previousInfo.is_net_receiver;
    }
    return false;
  }

  function getExclusionReason() {
    if (
      date?.getFullYear() === getCurrentDatePlusDays(0).getFullYear() &&
      date?.getMonth() === getCurrentDatePlusDays(0).getMonth()
    ) {
      return store?.non_usage_fee_info?.find((i) => i?.container_type_id === -1)
        ?.exclusion_reason;
    }
    const previousInfo = store?.all_non_usage_fee_info.previous_info.find(
      (i) => i.year === date?.getFullYear() && i.month === date.getMonth() + 1,
    );
    return previousInfo?.exclusion_reason;
  }

  function getMonthFormatted() {
    return date?.toLocaleDateString(
      i18n.language,
      getDateFormatOptions(false, i18n.language, true),
    );
  }

  const header = (data: string) => (
    <div className="align-items-center export-buttons flex">
      <Button
        type="button"
        icon={<FaFileDownload />}
        onClick={() => exportCSV(data)}
        className="me-2"
        data-pr-tooltip="CSV"
      />
      <Button
        type="button"
        icon={<FaFileExcel />}
        onClick={() => exportExcel(data)}
        className="p-button-success me-2"
        data-pr-tooltip="XLS"
      />
      {data === 'nonUsageFee' && (
        <p style={{ float: 'right' }}>{getMonthFormatted()}</p>
      )}
    </div>
  );

  const renderNonUsageInfo = () => {
    if (
      store == null ||
      (store.non_usage_fee_enabled !== true && !IsVytalEmployee(viewer))
    ) {
      return null;
    }
    return (
      <div>
        <div
          style={{ display: 'flex', alignItems: 'center' }}
          className="col-sm"
        >
          <h4 className="mb-0 mt-5">{i18n.t('Stock:allowance')}</h4>
        </div>
        <div style={{ float: 'right' }} className="mb-2">
          <Popover position="bottom" withArrow shadow="md">
            <Popover.Target>
              <Button>
                <FaCalendar className="me-2" />
                {i18n.t('Stock:selectMonth')}
              </Button>
            </Popover.Target>
            <Popover.Dropdown>
              <MonthPicker
                value={date}
                onChange={setDate}
                minDate={getMinDate()}
                maxDate={getCurrentDatePlusDays(0)}
              />
            </Popover.Dropdown>
          </Popover>
        </div>
        <DataTable
          value={getInfo()}
          header={header('nonUsageFee')}
          dataKey="id"
          responsiveLayout="scroll"
          lazy
          loading={store.non_usage_fee_info == null}
          className="p-datatable-striped"
          style={{ width: '100%', overflowX: 'scroll' }}
        >
          <Column
            headerStyle={{ width: '200px' }}
            body={(s: NonUsageFeeInfo) => s.container_type_name}
            header={i18n.t('Common:name')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(s: NonUsageFeeInfo) => s.stock}
            header={i18n.t('Stock:nufStock')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(s: NonUsageFeeInfo) => s.avg_weekly_checkouts}
            header={i18n.t('Stock:avgWeeklyCheckouts')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(s: NonUsageFeeInfo) => s.allowance || '-'}
            header={i18n.t('Stock:allowance')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(s: NonUsageFeeInfo) =>
              centsToPrice(s.non_return_cost, Currency.EUR) || '-'
            }
            header={i18n.t('Stock:nonReturnCost')}
          />
        </DataTable>
        <h5 className="mt-5">{i18n.t('Stock:netReceiver')}</h5>
        <pre style={{ fontFamily: 'inherit' }}>
          {isNetReceiver()
            ? i18n.t('Stock:isNetReceiver', {
                month: getMonthFormatted(),
              })
            : i18n.t('Stock:notIsNetReceiver', {
                month: getMonthFormatted(),
              })}
        </pre>
        <h5 className="mt-5">{i18n.t('Stock:isExcluded')}</h5>
        {getExclusionReason() != null ? (
          <pre style={{ fontFamily: 'inherit' }}>
            {i18n.t('Stock:exclusionReason', {
              month: getMonthFormatted(),
              reason: getExclusionReason(),
            })}
          </pre>
        ) : (
          <pre style={{ fontFamily: 'inherit' }}>
            {i18n.t('Stock:notExcluded', {
              month: getMonthFormatted(),
            })}
          </pre>
        )}
        <h5 className="mt-5">{i18n.t('Stock:tableExplanation')}</h5>
        <pre style={{ fontFamily: 'inherit' }}>
          {i18n.t('Stock:nonUsageFeeHint')}
        </pre>
      </div>
    );
  };

  return (
    <ContentSection title={props.title} collapsable={false} key="content">
      <div>
        <div
          style={{ display: 'flex', alignItems: 'center' }}
          className="col-sm"
        >
          <h4 className="mb-0">{i18n.t('Common:stock')}</h4>
          <Button
            label={i18n.t('MerchantOrder:orderContainers')}
            onClick={() => history.push(Routes.createOrder(props.vytalStoreId))}
            style={{ marginLeft: 'auto', order: 2 }}
          />
        </div>
        <Tooltip target=".export-buttons>button" position="bottom" />

        <DataTable
          value={stock}
          header={header('stock')}
          dataKey="id"
          responsiveLayout="scroll"
          lazy
          loading={isLoading}
          className="p-datatable-striped"
          style={{ width: '100%', overflowX: 'scroll' }}
        >
          <Column
            headerStyle={{ width: '200px' }}
            body={(s: Stock) => s.name}
            header={i18n.t('Common:name')}
          />
          <Column
            headerStyle={{ width: '200px' }}
            body={(s: Stock) => s.amount}
            header={i18n.t('Common:amount')}
          />
        </DataTable>
        {renderNonUsageInfo()}
      </div>
    </ContentSection>
  );
};
